import * as React from 'react';
import { cn } from '../../lib/utils';
import { PageHeader, type PageHeaderProps } from './page-header';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';

export interface ModuleTab {
  key: string;
  label: string;
  badge?: number;
  content: React.ReactNode;
}

export interface ModuleTabsProps
  extends Omit<PageHeaderProps, 'children'> {
  tabs: ModuleTab[];
  defaultTab?: string;
  onTabChange?: (tab: string) => void;
}

const ModuleTabs = React.forwardRef<HTMLDivElement, ModuleTabsProps>(
  (
    {
      title,
      subtitle,
      icon,
      actions,
      backHref,
      onBack,
      tabs,
      defaultTab,
      onTabChange,
      className,
      ...props
    },
    ref,
  ) => {
    const [activeTab, setActiveTab] = React.useState(
      defaultTab || tabs[0]?.key || '',
    );

    const handleTabChange = (tab: string) => {
      setActiveTab(tab);
      onTabChange?.(tab);
    };

    return (
      <div
        ref={ref}
        className={cn('flex flex-col h-full bg-[var(--color-background)]', className)}
        {...props}
      >
        <PageHeader
          title={title}
          subtitle={subtitle}
          icon={icon}
          actions={actions}
          backHref={backHref}
          onBack={onBack}
          className="flex-shrink-0"
        />

        <div className="flex-1 overflow-hidden">
          <Tabs
            value={activeTab}
            onValueChange={handleTabChange}
            className="flex h-full flex-col"
          >
            <div className="border-b border-[var(--color-border)] bg-[var(--color-background)] px-6">
              <TabsList className="h-auto gap-0 rounded-none border-0 bg-transparent p-0">
                {tabs.map((tab) => (
                  <TabsTrigger
                    key={tab.key}
                    value={tab.key}
                    className="relative gap-2 rounded-none border-b-2 border-transparent px-0 py-3 font-medium text-[var(--color-muted-foreground)] data-[state=active]:border-[var(--color-primary)] data-[state=active]:text-[var(--color-foreground)] data-[state=active]:shadow-none"
                  >
                    {tab.label}
                    {tab.badge !== undefined && tab.badge > 0 && (
                      <Badge variant="secondary" className="ml-1 h-6 w-6 p-0 flex items-center justify-center text-xs">
                        {tab.badge}
                      </Badge>
                    )}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            <div className="flex-1 overflow-auto">
              {tabs.map((tab) => (
                <TabsContent
                  key={tab.key}
                  value={tab.key}
                  className="m-0 h-full"
                >
                  {tab.content}
                </TabsContent>
              ))}
            </div>
          </Tabs>
        </div>
      </div>
    );
  },
);

ModuleTabs.displayName = 'ModuleTabs';

export { ModuleTabs };
